import type { PortalTheme } from '@/schema/gift-portal.schema'

export function portalCssVars(
  theme: PortalTheme,
  opts?: { standalone?: boolean },
): string {
  const c = theme.colors
  const standalone = opts?.standalone ?? true
  let css = ''
  for (const f of theme.fontFiles) {
    css += `@font-face { font-family: "PortalFont"; src: url(${f.url}); font-weight: ${f.weight}; font-display: swap; }\n`
  }
  css += `.portal-theme {
  --portal-bg: ${c.background};
  --portal-fg: ${c.foreground};
  --portal-card-bg: ${c.cardBackground};
  --portal-card-fg: ${c.cardForeground};
  --portal-primary: ${c.primary};
  --portal-btn: ${c.button};
  --portal-font: ${theme.fontFamily};
  --portal-bg-image: ${
    theme.backgroundUrl ? `url(${theme.backgroundUrl})` : 'none'
  };
}`
  if (standalone) {
    // Hang the background on <html> with background-attachment: fixed so
    // `cover` sizes against the viewport rather than the page's full height.
    // Otherwise a tall page scales the image up and makes it look zoomed in.
    css += `
html, body { margin: 0; padding: 0; min-height: 100%; }
html {
  background-color: ${c.background};
  background-image: ${
    theme.backgroundUrl ? `url(${theme.backgroundUrl})` : 'none'
  };
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
}
body { background: transparent; }`
  }
  return css
}

export const PORTAL_CSS = `
/* User-agent [hidden] { display: none } loses to our flex/grid rules below.
   Re-assert it so elements with the hidden attribute actually stay hidden. */
.portal-theme [hidden] { display: none !important; }

.portal-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--portal-font);
  padding: 1rem;
  box-sizing: border-box;
}
.portal-page--wide {
  min-height: 100vh;
  font-family: var(--portal-font);
  padding: 2rem 1rem;
  box-sizing: border-box;
}
/* When the page is embedded in the admin preview scaler, the admin owns
   <html>, so the portal needs its own background and shouldn't be 100vh. */
.portal-preview .portal-page,
.portal-preview .portal-page--wide {
  background-color: var(--portal-bg);
  background-image: var(--portal-bg-image);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  min-height: 100%;
  height: 100%;
  width: 100%;
}
.portal-container {
  max-width: 960px;
  margin: 0 auto;
}
.portal-card {
  background-color: var(--portal-card-bg);
  border-radius: 12px;
  padding: 2.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.portal-card--center {
  text-align: center;
}
/* Text on a card surface (Access card, success panel) follows Card text
   colour so light-text-on-dark-card stays readable. Text on the page
   background keeps the page Text colour via the base rules above. Gift tiles
   (.portal-gift) and the redeem dialog (.portal-dialog) set this directly. */
.portal-card .portal-title,
.portal-card .portal-title--spaced,
.portal-card .portal-text,
.portal-card .portal-text--last,
.portal-card .portal-label {
  color: var(--portal-card-fg);
}
.portal-logo {
  max-height: 48px;
  margin-bottom: 1.5rem;
  object-fit: contain;
}
.portal-title {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  line-height: 2.5rem;
  font-weight: 700;
  color: var(--portal-fg);
}
.portal-title--spaced {
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--portal-fg);
}
.portal-text {
  margin: 0 0 1.5rem;
  color: var(--portal-fg);
  opacity: 0.75;
  font-size: 1rem;
  line-height: 1.5;
}
.portal-text--last {
  margin: 0;
  color: var(--portal-fg);
  font-size: 0.95rem;
  line-height: 1.6;
}
.portal-alert {
  background-color: #fef2f2;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}
.portal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.portal-label {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--portal-fg);
}
/* Inputs keep a fixed white surface, so their text and placeholder are fixed
   neutrals rather than theme colours — a light Text colour must not make
   typed text invisible on the white field (Safari also derives placeholder
   colour from the input colour). */
.portal-input {
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1rem;
  outline: none;
  background: #fff;
  color: #1a1a2e;
  font-family: inherit;
}
.portal-input::placeholder,
.portal-email-bar__input::placeholder,
.portal-email-dialog__input::placeholder {
  color: #9ca3af;
  opacity: 1;
}
.portal-input:focus {
  border-color: var(--portal-primary);
}
.portal-input--sm {
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  font-size: 0.875rem;
}
.portal-btn {
  margin-top: 0.5rem;
  padding: 0.625rem 1rem;
  background-color: var(--portal-btn);
  color: #fff;
  border: none;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: opacity 0.15s;
}
.portal-btn:hover { opacity: 0.9; }
.portal-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.portal-btn--full {
  width: 100%;
  margin-top: 0;
  height: 36px;
  padding: 0.5rem 1rem;
}
.portal-btn--inline {
  display: inline-block;
  margin-top: 0;
  padding: 0.625rem 1.5rem;
  font-size: 0.9rem;
  text-decoration: none;
}
.portal-btn--outline {
  background-color: #fff;
  color: #1a1a2e;
  border: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: 0;
  height: 36px;
  padding: 0.5rem 1rem;
}
.portal-btn--outline:hover { background-color: rgba(0, 0, 0, 0.03); opacity: 1; }
.portal-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}
.portal-icon--success { background-color: #dcfce7; color: #16a34a; }
.portal-icon--error { background-color: #fef2f2; color: #dc2626; font-weight: 700; }

/* --- Select page header & email bar --- */
.portal-select-header {
  display: flex;
  gap: 2rem;
  align-items: flex-end;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}
.portal-select-header__intro {
  flex: 1 1 420px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.portal-select-header__intro .portal-title,
.portal-select-header__intro .portal-text { margin: 0; }

.portal-email-bar {
  /* ~400px at the default root size, but in rem so it tracks the theme font,
     and it may shrink to the header's own wrap point on narrow screens. */
  flex: 0 1 25rem;
  min-width: min(100%, 17rem);
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  background-color: var(--portal-card-bg);
  border-radius: 8px;
  box-sizing: border-box;
}
.portal-email-bar__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.portal-email-bar__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--portal-card-fg);
  line-height: 1;
}
.portal-email-bar__value {
  font-size: 0.875rem;
  line-height: 1.43;
  color: var(--portal-card-fg);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.portal-email-bar__value[data-empty="1"] { font-style: italic; }
.portal-email-bar__value[data-empty="0"] .portal-email-bar__value-empty { display: none; }
.portal-email-bar__value[data-empty="1"] .portal-email-bar__value-actual { display: none; }
.portal-email-bar__change {
  color: var(--portal-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  white-space: nowrap;
  background: transparent;
  border: none;
  font-family: inherit;
  height: 36px;
}
.portal-email-bar__change:hover { background-color: rgba(0, 0, 0, 0.04); }

.portal-email-bar[data-mode="edit"] { align-items: flex-end; }
.portal-email-bar__input {
  width: 100%;
  height: 36px;
  padding: 0.25rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 0.875rem;
  color: #1a1a2e;
  outline: none;
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}
.portal-email-bar__input:focus { border-color: var(--portal-primary); }
.portal-email-bar__actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}
.portal-email-bar__action {
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  cursor: pointer;
  background: transparent;
  border: none;
  font-family: inherit;
  height: 36px;
  white-space: nowrap;
}
.portal-email-bar__action--cancel { color: var(--portal-card-fg); }
.portal-email-bar__action--save { color: var(--portal-primary); }

/* --- Gift grid --- */
.portal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
}
.portal-gift {
  background-color: var(--portal-card-bg);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}
.portal-gift__img { width: 100%; aspect-ratio: 2 / 1; height: auto; object-fit: contain; display: block; }
.portal-gift__body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.5rem 1.5rem 0;
  flex: 1;
}
.portal-gift__name {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 600;
  color: var(--portal-card-fg);
}
.portal-gift__desc {
  margin: 0;
  color: var(--portal-card-fg);
  opacity: 0.6;
  font-size: 0.875rem;
  line-height: 1.4;
}
.portal-gift__footer { padding: 1rem 1.5rem 1.5rem; }
.portal-gift--selected {
  border-color: var(--portal-btn);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}
.portal-gift:not(.portal-gift--selected) .portal-btn:hover { opacity: 0.7; }
.portal-gift .portal-gift__chosen-label { display: none; }
.portal-gift--selected .portal-gift__choose-label { display: none; }
.portal-gift--selected .portal-gift__chosen-label { display: inline; }
.portal-empty { text-align: center; color: var(--portal-fg); padding: 3rem 0; }

/* --- Claim bar (bottom action) --- */
.portal-claim-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
}
.portal-claim-bar__btn {
  min-width: 240px;
  height: 44px;
}
@media (max-width: 640px) {
  .portal-claim-bar {
    position: sticky;
    bottom: 0;
    margin: 1.5rem -1rem 0;
    padding: 1rem;
    background-color: var(--portal-bg);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    z-index: 10;
  }
  .portal-claim-bar__btn { width: 100%; min-width: 0; }
}

/* --- Redeem dialog (native <dialog>) --- */
.portal-dialog {
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background-color: var(--portal-card-bg);
  color: var(--portal-card-fg);
  font-family: var(--portal-font);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: min(34rem, calc(100vw - 2rem));
  width: 100%;
  /* A dialog is centred by the UA, but a form taller than the viewport must
     still be reachable — cap it and let the body scroll inside. */
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}
.portal-dialog::backdrop { background-color: rgba(0, 0, 0, 0.4); }
.portal-dialog__body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  padding: 1.5rem;
  /* A six-field form plus an on-screen keyboard does not fit a phone in
     landscape, so the body is the scroll container. */
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.portal-dialog__header { display: flex; flex-direction: column; gap: 0.5rem; }
.portal-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.55;
  font-weight: 600;
  color: var(--portal-card-fg);
}
.portal-dialog__subtitle {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.43;
  color: var(--portal-card-fg);
  opacity: 0.8;
}
.portal-dialog__footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}
.portal-dialog .portal-email-bar { width: 100%; flex: 1 1 auto; }

/* --- Email-change dialog (dedicated to editing email) --- */
.portal-email-dialog__label {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--portal-card-fg);
}
.portal-email-dialog__label-text { font-weight: 500; opacity: 0.85; }
.portal-email-dialog__error {
  font-size: 0.75rem;
  line-height: 1.3;
  color: #b3261e;
}
[data-email-invalid='0'] .portal-email-dialog__error {
  display: none;
}
[data-email-invalid='1'] .portal-email-dialog__input {
  border-color: #b3261e;
}
.portal-email-dialog__input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background-color: #fff;
  color: #1a1a2e;
  font-family: var(--portal-font);
  font-size: 0.9375rem;
  outline: none;
}
.portal-email-dialog__input:focus { border-color: var(--portal-primary); }

/* --- Address window ---
   One state: the prefilled form. [data-address-empty] is the only switch, and
   it swaps the subtitle for the "nothing on file" notice. Nothing here is
   toggled by writing labels. */
.portal-address-dialog__notice {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.43;
  color: var(--portal-card-fg);
  opacity: 0.8;
}
.portal-address-dialog__form {
  display: grid;
  /* Two even columns for the paired fields; name and street span both. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  /* The row gap has to beat the label-to-input gap by enough that a label
     reads as belonging to the field below it, not the box above. */
  gap: 1.25rem 0.75rem;
}
.portal-address-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}
.portal-address-dialog__form [data-address-field-wrap='name'],
.portal-address-dialog__form [data-address-field-wrap='streetName'] {
  grid-column: 1 / -1;
}
.portal-address-dialog__label {
  display: flex;
  align-items: baseline;
  gap: 0.1875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
  color: var(--portal-card-fg);
  opacity: 0.85;
}
.portal-address-dialog__required { color: #b3261e; font-weight: 600; }
.portal-address-dialog__input {
  width: 100%;
  box-sizing: border-box;
  /* 2.75rem tall: a comfortable tap target, and the same height whether or
     not the field below it is showing an error. */
  min-height: 2.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.28);
  border-radius: 8px;
  background-color: #fff;
  color: #1a1a2e;
  font-family: var(--portal-font);
  font-size: 1rem;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.portal-address-dialog__input:focus {
  border-color: var(--portal-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--portal-primary) 22%, transparent);
}
.portal-address-dialog__error {
  font-size: 0.75rem;
  line-height: 1.3;
  color: #b3261e;
}

/* Error text and the red field only appear once a required field is
   actually empty at save time. */
.portal-address-dialog__field:not([data-address-invalid='1'])
  .portal-address-dialog__error {
  display: none;
}
[data-address-invalid='1'] .portal-address-dialog__input {
  border-color: #b3261e;
}
[data-address-invalid='1'] .portal-address-dialog__input:focus {
  box-shadow: 0 0 0 3px rgba(179, 38, 30, 0.18);
}

.portal-address-dialog__footer {
  gap: 0.625rem;
  margin-top: 0.25rem;
}

.portal-address-dialog[data-address-empty='0'] .portal-address-dialog__notice,
.portal-address-dialog[data-address-empty='1'] .portal-dialog__subtitle {
  display: none;
}

@media (max-width: 560px) {
  .portal-address-dialog__form { grid-template-columns: minmax(0, 1fr); }
  .portal-dialog__body { padding: 1.25rem; gap: 0.875rem; }
  /* Six stacked fields are taller than a phone screen, so the actions have
     to stay put while the fields scroll under them — otherwise the primary
     button sits below the fold and has to be hunted for. The negative
     margins let the bar span the body's padding edge to edge. */
  .portal-address-dialog__footer {
    flex-direction: column-reverse;
    position: sticky;
    bottom: 0;
    z-index: 1;
    margin: 0 -1.25rem -1.25rem;
    padding: 0.875rem 1.25rem 1.25rem;
    background-color: var(--portal-card-bg);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .portal-address-dialog__footer .portal-btn { width: 100%; }
}

/* --- Confirmed address on the success page --- */
.portal-success__address {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: var(--portal-card-fg);
}
.portal-success__address-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
}

/* --- Success page (Figma 25361:57573) --- */
.portal-success {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: var(--portal-font);
  box-sizing: border-box;
  padding: 1rem;
}
.portal-preview .portal-success {
  background-color: var(--portal-bg);
  background-image: var(--portal-bg-image);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  min-height: 100%;
  height: 100%;
  width: 100%;
}
.portal-success__content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1232px;
  margin: 0 auto;
  padding: 3.5rem 1.5rem;
  box-sizing: border-box;
}
.portal-success__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
  max-width: 520px;
}
.portal-success__icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background-color: color-mix(in srgb, var(--portal-primary) 12%, #ffffff);
  color: var(--portal-fg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}
.portal-success__text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
}
.portal-success__title {
  margin: 0;
  font-size: 1.875rem;
  line-height: 1;
  font-weight: 600;
  color: var(--portal-card-fg);
}
.portal-success__body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.43;
  color: color-mix(in srgb, var(--portal-card-fg) 55%, transparent);
}
.portal-success__body .portal-success-body__email {
  font-weight: 500;
  color: var(--portal-card-fg);
  word-break: break-all;
}

.portal-footer__links { display: flex; gap: 2rem; align-items: center; }

/* --- Language toggle --- */
.portal-lang { position: fixed; top: 1rem; right: 1rem; z-index: 100; display: flex; align-items: center; gap: 0.5rem; }
.portal-lang__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--portal-fg, #1a1a2e);
  font-family: var(--portal-font);
}
.portal-lang__select {
  background: var(--portal-card-bg, #fff);
  color: var(--portal-card-fg, #1a1a2e);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 0.375rem 1.75rem 0.375rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--portal-font);
  letter-spacing: 0.5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M0%200l5%206%205-6z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 0.625rem;
}
.portal-lang__select:focus { outline: 2px solid var(--portal-primary, #1a73e8); outline-offset: 2px; }
`
